import { FormControl, FormLabel, FormErrorMessage, Box, Text } from '@chakra-ui/react';
import { useState, useCallback } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

export default function PhoneNumberInput({
    formLabel,
    name,
    isInvalid,
    value,
    onChange,
    disabled,
    formErroMsg,
    w,
    required = true,
    autoFocus = false,
    enableValidation = true,
    ...rest
}) {
    const [isTouched, setIsTouched] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('gb');

    // Phone number validation based on selected country
    const isValidPhoneNumber = useCallback((phone, countryCode) => {
        if (!phone) return false;
        
        const digitsOnly = phone.replace(/\D/g, '');
        
        // Basic length validation by country
        const countryLengths = {
            'gb': { min: 10, max: 11 }, // UK: 10-11 digits without country code
            'us': { min: 10, max: 10 }, // US: 10 digits
            'ca': { min: 10, max: 10 }, // Canada: 10 digits
            'au': { min: 9, max: 9 },   // Australia: 9 digits
            'fr': { min: 9, max: 9 },   // France: 9 digits
            'de': { min: 10, max: 11 }, // Germany: 10-11 digits
            'es': { min: 9, max: 9 },   // Spain: 9 digits
            'it': { min: 9, max: 10 },  // Italy: 9-10 digits
            'nl': { min: 9, max: 9 },   // Netherlands: 9 digits
            'br': { min: 10, max: 11 }, // Brazil: 10-11 digits
            'in': { min: 10, max: 10 }, // India: 10 digits
            'cn': { min: 11, max: 11 }, // China: 11 digits
            'jp': { min: 10, max: 11 }, // Japan: 10-11 digits
        };

        const lengths = countryLengths[countryCode] || { min: 8, max: 15 };
        const numberWithoutCountryCode = digitsOnly.replace(/^44|^1|^49|^33|^34|^39|^31|^55|^91|^86|^81/, '');
        
        return numberWithoutCountryCode.length >= lengths.min && 
               numberWithoutCountryCode.length <= lengths.max;
    }, []);

    // Get country-specific validation message
    const getValidationMessage = useCallback((countryCode) => {
        const messages = {
            'gb': "UK phone numbers should be 10-11 digits (e.g., 7123 456 789)",
            'us': "US phone numbers should be 10 digits (e.g., 201 555 0123)",
            'ca': "Canadian phone numbers should be 10 digits (e.g., 204 555 0123)",
            'au': "Australian phone numbers should be 9 digits (e.g., 2 3456 7890)",
            'fr': "French phone numbers should be 9 digits (e.g., 1 23 45 67 89)",
            'de': "German phone numbers should be 10-11 digits (e.g., 151 12345678)",
            'es': "Spanish phone numbers should be 9 digits (e.g., 612 34 56 78)",
            'it': "Italian phone numbers should be 9-10 digits (e.g., 312 345 6789)",
            'nl': "Dutch phone numbers should be 9 digits (e.g., 6 12345678)",
            'br': "Brazilian phone numbers should be 10-11 digits (e.g., 11 98765 4321)",
            'in': "Indian phone numbers should be 10 digits (e.g., 98765 43210)",
            'cn': "Chinese phone numbers should be 11 digits (e.g., 131 2345 6789)",
            'jp': "Japanese phone numbers should be 10-11 digits (e.g., 90 1234 5678)",
        };
        
        return messages[countryCode] || "Please enter a valid phone number for the selected country";
    }, []);

    // Get country-specific format example
    const getFormatExample = useCallback((countryCode) => {
        const examples = {
            'gb': "+44 7123 456 789",
            'us': "+1 201 555 0123", 
            'ca': "+1 204 555 0123",
            'au': "+61 2 3456 7890",
            'fr': "+33 1 23 45 67 89",
            'de': "+49 151 12345678",
            'es': "+34 612 34 56 78",
            'it': "+39 312 345 6789",
            'nl': "+31 6 12345678",
            'br': "+55 11 98765 4321",
            'in': "+91 98765 43210",
            'cn': "+86 131 2345 6789",
            'jp': "+81 90 1234 5678",
        };
        
        return examples[countryCode] || "+XXX XXX XXX XXX";
    }, []);

    const handlePhoneChange = (value, country, e, formattedValue) => {
        // Update selected country when user changes country
        if (country && country.countryCode) {
            setSelectedCountry(country.countryCode.toLowerCase());
        }
        
        // Call the original onChange handler
        if (onChange) {
            onChange(value, country, e, formattedValue);
        }
    };

    const handleBlur = () => {
        setIsTouched(true);
    };

    const handleFocus = () => {
        if (!value) {
            setSelectedCountry('gb'); // Reset to UK default if no value
        }
    };

    // Determine if we should show error
    const showError = enableValidation && (isInvalid || (isTouched && value && !isValidPhoneNumber(value, selectedCountry)));
    const isValid = enableValidation && value && isValidPhoneNumber(value, selectedCountry);

    return (
        <FormControl isInvalid={showError} w={w}>
            <FormLabel fontSize="12pt" mb={2}>
                {formLabel}
                {required && <Box as="span" color="red.500" ml={1}>*</Box>}
            </FormLabel>
            
            <Box 
                border={showError ? "2px solid" : "1px solid"}
                borderColor={showError ? "red.500" : "gray.200"}
                borderRadius="md"
                _focusWithin={{
                    borderColor: showError ? "red.500" : "blue.500",
                    boxShadow: showError ? "0 0 0 1px red.500" : "0 0 0 1px blue.500"
                }}
                transition="all 0.2s"
                bg={disabled ? "gray.50" : "white"}
            >
                <PhoneInput
                    name={name}
                    country={"gb"} // UK as default
                    enableAreaCodes={true}
                    enableSearch={true}
                    disableSearchIcon={false}
                    preferredCountries={['gb', 'us', 'ca', 'au', 'fr', 'de']}
                    inputProps={{
                        name: name,
                        required: required,
                        autoFocus: autoFocus,
                        disabled: disabled,
                        onBlur: handleBlur,
                        onFocus: handleFocus,
                    }}
                    value={value}
                    onChange={handlePhoneChange}
                    specialLabel=""
                    inputStyle={{
                        width: "100%",
                        height: "40px",
                        fontSize: "14px",
                        border: "none",
                        background: "transparent",
                    }}
                    buttonStyle={{
                        border: "none",
                        background: "transparent",
                    }}
                    dropdownStyle={{
                        fontSize: "14px",
                        zIndex: 1500,
                    }}
                    containerStyle={{
                        width: "100%",
                    }}
                    autoFormat={true}
                    disableCountryGuess={false}
                    countryCodeEditable={true}
                    enableTerritories={true}
                    {...rest}
                />
            </Box>

            {/* Country-specific format hint */}
            {!showError && !isValid && value && (
                <Text fontSize="11pt" color="gray.600" mt={1}>
                    Format: {getFormatExample(selectedCountry)}
                </Text>
            )}

            {/* Validation messages */}
            {showError && (
                <FormErrorMessage fontSize="11pt" mt={1}>
                    {formErroMsg || getValidationMessage(selectedCountry)}
                </FormErrorMessage>
            )}
            
            {/* {isValid && (
                <Box fontSize="11pt" color="green.500" mt={1}>
                    ✓ Valid  phone number
                </Box>
            )} */}
        </FormControl>
    );
}